import { DateValue } from '@givebutter/primitives/date-picker';
import { maskWholeNumber, unmaskNumber } from '@givebutter/util';
import { CustomFieldValue } from 'components/filter/filters-next/custom-field-types';
import {
    transformDateOperatorToReadable,
    transformDateToReadable,
    transformMultiselectToReadable,
    transformOperatorToReadable,
} from 'components/filter/filters-next/readable';
import {
    transformDateToRequest,
    transformValueToRequest,
} from 'components/filter/filters-next/util';
import { FilterOperator, FilterValue } from 'components/filter/types';
import { identity } from 'lodash';
import { DateTime } from 'luxon';
import { CustomFieldType } from 'types/filter';

const getDefaultValueByType = (type: CustomFieldType) => {
    switch (type) {
        case 'checkbox':
            return false;
        case 'date':
        case 'daterange':
            return DateTime.now();
        case 'select':
        case 'multiselect':
            return [];
        case 'textarea':
        case 'text':
        case 'file':
        case 'radio':
        case 'percentage':
        case 'number':
        default:
            return '';
    }
};

const getDefaultFilterOperatorByType = (type: CustomFieldType): FilterOperator => {
    switch (type) {
        case 'checkbox':
        case 'select':
            return 'eq';
        case 'multiselect':
            return 'all';
        case 'date':
        case 'daterange':
        case 'percentage':
        case 'number':
            return 'gt';
        case 'textarea':
        case 'text':
        case 'file':
        case 'radio':
        default:
            return 'includes';
    }
};

const transformOperatorReadableByType = (filter: FilterValue<CustomFieldValue>) => {
    if (!filter.filterOperator) {
        return '';
    }

    switch (filter.value.type) {
        case 'checkbox':
        case 'select':
            return '';
        case 'date':
        case 'daterange':
            return transformDateOperatorToReadable(filter.filterOperator);
        case 'percentage':
        case 'multiselect':
        case 'number':
        case 'textarea':
        case 'text':
        case 'file':
        case 'radio':
        default:
            return transformOperatorToReadable(filter.filterOperator);
    }
};

const transformReadableByType = (filter: FilterValue<CustomFieldValue>) => {
    switch (filter.value.type) {
        case 'checkbox':
            return filter.value.value ? 'is Yes' : 'is No';
        case 'date':
        case 'daterange':
            return transformDateToReadable(filter.value.value as DateValue);
        case 'multiselect':
        case 'select':
            return transformMultiselectToReadable(filter.value.value as string[]);
        case 'number':
            return maskWholeNumber(filter.value.value as string);
        case 'percentage':
            return `${filter.value.value as number}%`;
        case 'radio':
        case 'textarea':
        case 'text':
        case 'file':
            return filter.value.value as string;
    }
};

const transformUpstreamByType = (filter: FilterValue<CustomFieldValue>) => {
    switch (filter.value.type) {
        case 'date':
        case 'daterange':
            return transformDateToRequest(
                filter.value.value as DateValue,
                'and',
                filter.filterOperator,
                false,
            );
        case 'select':
        case 'checkbox':
            return transformValueToRequest(filter.value.value, 'and', 'eq');
        default:
            return transformValueToRequest(
                filter.value.value as string,
                'and',
                filter.filterOperator,
                maskFnFactory(filter.value.type),
            );
    }
};
const maskFnFactory = (type: CustomFieldType) => {
    switch (type) {
        case 'number':
        case 'percentage':
            return unmaskNumber;
        default:
            return identity;
    }
};

export {
    getDefaultFilterOperatorByType,
    getDefaultValueByType,
    transformUpstreamByType,
    transformReadableByType,
    transformOperatorReadableByType,
};
